import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';
import { cacheSupportQueueScoping } from '../utils/platformPermissions';

// Well-known feature keys consumed across the app. Keep the list in one
// place so the loader, the admin Settings page, and the gates all agree.
export const FEATURE_KEYS = {
  doctorAllPatientMode: 'doctor.allPatientMode',
  // doctor.patientLibrary removed (India-SaaS decisions log #18).
  // patient.allDoctorMode, patient.doctorLibrary and patient.familyAccounts
  // are retired (owner, 1 Oct 2026): the first two are gone, family accounts
  // are always on.
  // Multi-value (not boolean) setting: how far in the future a doctor may apply
  // for leave. Stored in the feature row's `value` column as one of
  // '3' | '6' | '9' | '12' | 'none' (months, or 'none' = no restriction).
  // The leave date picker reads this to cap its max selectable date.
  doctorLeaveFutureLimit: 'doctor.leaveFutureLimit',
  // Gate the public patient registration flow. When inactive, the Home/Login
  // CTAs and the corresponding card in RegisterChoice are hidden, and the
  // route itself redirects away. Default-on so existing deployments still
  // allow sign-up until an admin explicitly disables it.
  // auth.doctorRegistration is NOT a platform flag any more (owner, 1 Oct
  // 2026): it is per tenant, set from Tenants → Login Policy, so it is not
  // fetched or toggled through this slice.
  authPatientRegistration: 'auth.patientRegistration',
  // doctor.medicines is retired (owner, 1 Oct 2026): Medicines is always on
  // and follows only the tenant's `medicines` feature.
  // Multi-value: the platform-wide support response SLA used for tickets whose
  // raiser has no plan-level SLA (MOC patients) and whose hospital has no
  // `supportLevel` of its own. Stored in the row's `value` as one of the
  // SUPPORT_SLA_OPTIONS values; '48h' when unset. Super Admin only — the
  // backend lists it as PLATFORM_OWNED (config/featureOwnership.js).
  supportDefaultSla: 'support.defaultSla',
  // Super-admin-only support switches (doc/support-single-tier-plan.md). Both
  // default ON so an existing deployment behaves exactly as before the flag.
  //   support.autoAssign   — ON: a new ticket goes to the least-loaded platform
  //                          agent; OFF: it stays Unassigned until someone
  //                          accepts it. Read only by the backend at ticket
  //                          creation; the SA app just offers the switch.
  //   support.queueScoping — ON: an agent needs support.queue.<queue> on top of
  //                          support.view / support.respond; OFF: view/respond
  //                          alone open BOTH queues. The server's canViewQueue
  //                          is the real gate; this app mirrors it so the
  //                          sidebar/routes never offer (or hide) a queue the
  //                          socket would decide the other way.
  supportAutoAssign: 'support.autoAssign',
  supportQueueScoping: 'support.queueScoping',
  // NOTE: the login-identifier policy (auth.loginIdentifier + its two OTP
  // sub-options) is PER HOSPITAL and deliberately NOT in this slice — the
  // login/registration pages read each tenant's `loginPolicy` from
  // GET /tenants/public (see utils/loginPolicy.js), and the Super Admin sets
  // it from Tenants → Features.
};

const ALL_KEYS = Object.values(FEATURE_KEYS);

// Default status per key. Used while flags are still loading and as the
// fallback when the backend returns no row for a key.
const DEFAULT_STATUS = {
  [FEATURE_KEYS.doctorAllPatientMode]: 'active',
  [FEATURE_KEYS.doctorLeaveFutureLimit]: 'active',
  [FEATURE_KEYS.authPatientRegistration]: 'active',
  [FEATURE_KEYS.supportDefaultSla]: 'active',
  [FEATURE_KEYS.supportAutoAssign]: 'active',
  [FEATURE_KEYS.supportQueueScoping]: 'active',
};

// Fallback values for multi-value (non-boolean) settings, used while flags load
// and when the backend has no row/value yet. Boolean flags stay null here.
const DEFAULT_VALUE = {
  [FEATURE_KEYS.doctorLeaveFutureLimit]: '12',
  [FEATURE_KEYS.supportDefaultSla]: '48h',
};

// Ordered stops for the "leave apply future upto" setting. The admin Settings
// slider renders these left→right; the leave picker maps the chosen `value` to
// a max selectable date. `months: null` means "no restriction".
export const LEAVE_FUTURE_LIMIT_OPTIONS = [
  { value: '3', months: 3 },
  { value: '6', months: 6 },
  { value: '9', months: 9 },
  { value: '12', months: 12 },
  { value: 'none', months: null },
];

// Ordered stops for the "default support SLA" slider — fastest first so the
// filled track reads as "how much of the maximum delay is allowed". Values are
// the backend's SUPPORT_TIERS (config/supportRaisers.js) verbatim; labelKey /
// labelFallback follow the shape Settings.jsx's sliderOptionLabel expects.
export const SUPPORT_SLA_OPTIONS = [
  { value: 'Immediate', labelKey: 'settings.sla_immediate', labelFallback: 'Dedicated (1h)' },
  { value: '12h', labelKey: 'settings.sla_12h', labelFallback: '12 hours' },
  { value: '24h', labelKey: 'settings.sla_24h', labelFallback: '24 hours' },
  { value: '48h', labelKey: 'settings.sla_48h', labelFallback: '48 hours' },
];


export const fetchFeatureFlags = createAsyncThunk(
  'feature/fetchFeatureFlags',
  async (_, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.get(
        `${baseURL}/feature/by-keys?keys=${encodeURIComponent(ALL_KEYS.join(','))}`
      );
      return response.data?.features || [];
    } catch (error) {
      return rejectWithValue({
        error: error.response?.data?.message || error.message || 'Failed to load feature flags',
      });
    }
  }
);

export const toggleFeatureStatus = createAsyncThunk(
  'feature/toggleFeatureStatus',
  async ({ featureId, fekey }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.post(
        `${baseURL}/feature/${featureId}/toggle-status`
      );
      return { fekey, feature: response.data?.feature };
    } catch (error) {
      return rejectWithValue({
        error: error.response?.data?.message || error.message || 'Failed to toggle feature',
      });
    }
  }
);

// Persist the scalar value of a multi-value setting (e.g. the leave future
// limit). Boolean flags keep using toggleFeatureStatus instead.
export const setFeatureValue = createAsyncThunk(
  'feature/setFeatureValue',
  async ({ featureId, fekey, value }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.post(
        `${baseURL}/feature/${featureId}/value`,
        { value }
      );
      return { fekey, feature: response.data?.feature };
    } catch (error) {
      return rejectWithValue({
        error: error.response?.data?.message || error.message || 'Failed to update setting',
      });
    }
  }
);

const initialState = {
  // Map of fekey → { status: 'active'|'inactive', featureId, value }
  flags: Object.fromEntries(
    ALL_KEYS.map((k) => [k, { status: DEFAULT_STATUS[k], featureId: null, value: DEFAULT_VALUE[k] ?? null }])
  ),
  loaded: false,
  loading: false,
  toggling: false,
  settingValue: false,
  error: null,
};

// Is queue-based agent access in force? Reads the slice state (or the whole
// root state — both shapes are accepted so `useSelector(isQueueScopingOn)`
// and `isQueueScopingOn(state.feature)` both work). Anything but an explicit
// 'inactive' counts as ON: while flags are still loading, or if the row is
// missing, the STRICTER rule applies, so a page is never offered that the
// server's canViewQueue would then refuse.
export const isQueueScopingOn = (featureState) => {
  const slice = featureState?.flags ? featureState : featureState?.feature;
  return slice?.flags?.[FEATURE_KEYS.supportQueueScoping]?.status !== 'inactive';
};

// Keep the localStorage mirror in step with the slice. The sync helpers in
// utils/platformPermissions.js (firstAllowedSupportRoute, canViewSupportQueue)
// run outside React's data flow — from App.jsx's "/" landing and the Login
// page's post-login redirect — and cannot read the store, so they read this
// mirror instead. A localStorage write is an odd thing for a reducer to do,
// but it is the one place every fresh flag list passes through, so the mirror
// can never drift from what Settings shows.
const mirrorQueueScoping = (state) => cacheSupportQueueScoping(isQueueScopingOn(state));

const featureSlice = createSlice({
  name: 'feature',
  initialState,
  reducers: {
    resetFeatureFlags: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatureFlags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        for (const row of action.payload) {
          if (!row || !row.fekey) continue;
          // Backend returns status: null when no row exists yet for that key.
          // Fall back to the documented default rather than leaving it null.
          state.flags[row.fekey] = {
            status: row.status || DEFAULT_STATUS[row.fekey] || 'inactive',
            featureId: row.featureId || null,
            value: row.value ?? DEFAULT_VALUE[row.fekey] ?? null,
          };
        }
        mirrorQueueScoping(state);
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to load feature flags';
      })
      .addCase(toggleFeatureStatus.pending, (state) => {
        state.toggling = true;
      })
      .addCase(toggleFeatureStatus.fulfilled, (state, action) => {
        state.toggling = false;
        const { fekey, feature } = action.payload || {};
        if (fekey && feature) {
          state.flags[fekey] = {
            status: feature.status,
            featureId: feature.featureId,
            value: feature.value ?? DEFAULT_VALUE[fekey] ?? null,
          };
        }
        mirrorQueueScoping(state);
      })
      .addCase(toggleFeatureStatus.rejected, (state, action) => {
        state.toggling = false;
        state.error = action.payload?.error || 'Failed to toggle feature';
      })
      .addCase(setFeatureValue.pending, (state) => {
        state.settingValue = true;
      })
      .addCase(setFeatureValue.fulfilled, (state, action) => {
        state.settingValue = false;
        const { fekey, feature } = action.payload || {};
        if (fekey && feature) {
          state.flags[fekey] = {
            status: feature.status,
            featureId: feature.featureId,
            value: feature.value ?? DEFAULT_VALUE[fekey] ?? null,
          };
        }
      })
      .addCase(setFeatureValue.rejected, (state, action) => {
        state.settingValue = false;
        state.error = action.payload?.error || 'Failed to update setting';
      });
  },
});

export const { resetFeatureFlags } = featureSlice.actions;

// Selector helper used by gating call-sites.
export const isFeatureEnabled = (state, fekey) =>
  state?.feature?.flags?.[fekey]?.status === 'active';

// Read a multi-value setting's stored value, falling back to its default.
export const getFeatureValue = (state, fekey) =>
  state?.feature?.flags?.[fekey]?.value ?? DEFAULT_VALUE[fekey] ?? null;

export default featureSlice.reducer;
